import React from 'react';
import { Box, BoxProps, styled, SxProps, Theme } from '@mui/material';
import colors from 'src/utils/colors';

interface StyledPillProps extends BoxProps {
  variant?: 'filled' | 'outlined';
  iconSx?: SxProps<Theme>;
  pillSx?: SxProps<Theme>;
  customIcon?: React.ReactNode;
}

interface ActionPillProps extends StyledPillProps {
  onRemovalClick?: () => void;
}

export const StyledPill = styled(Box)<StyledPillProps>(({ variant = 'outlined', pillSx = {}, iconSx = {} }) => ({
  borderRadius: '24px',
  backgroundColor: variant === 'filled' ? colors.lightOpaqueBlue : colors.opaqueGrey,
  padding: '4px 8px 4px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: variant === 'filled' ? colors.darkBlue : '#546379',
  lineHeight: '20px',
  whiteSpace: 'nowrap' as const,
  ...(pillSx as React.CSSProperties)
}));

/**
 * Inline circle-with-minus icon that replaces the SVG-sprite-based IconLess.
 * SVG sprites use <use xlinkHref> which dom-to-image cannot serialize
 * (the sprite definitions live outside the cloned subtree), so the icon
 * vanishes in "Copy Image" exports. Pure CSS/HTML renders correctly.
 */
const InlineMinusCircle: React.FC<{ style?: React.CSSProperties; onClick?: React.MouseEventHandler }> = ({
  style = {},
  onClick
}) => {
  const { color = colors.darkBlue, ...rest } = style;
  return (
    <div
      onClick={onClick}
      style={{
        width: 16,
        height: 16,
        borderRadius: '50%',
        backgroundColor: color as string,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        ...rest
      }}
    >
      <div
        style={{
          width: 6,
          height: 0,
          borderTop: '1px solid #fff',
          borderRadius: 1
        }}
      />
    </div>
  );
};

export const ActionPill: React.FC<ActionPillProps> = ({
  variant,
  pillSx = {},
  iconSx = {},
  onRemovalClick = () => {},
  customIcon,
  children
}) => {
  return (
    <StyledPill variant={variant} pillSx={pillSx}>
      {children}
      {customIcon ? (
        customIcon
      ) : (
        <InlineMinusCircle
          style={{
            color: variant === 'filled' ? colors.darkBlue : '#546379',
            ...(iconSx as React.CSSProperties)
          }}
          onClick={onRemovalClick}
        />
      )}
    </StyledPill>
  );
};
